/**
 * Componente: ObservacionesRecomendacionesDialog
 *
 * Modal para mostrar las observaciones y recomendaciones de un cumplimiento.
 *
 * Legacy: ListarCumplimientosViewImpl.java → líneas 378-416
 *
 * Funcionalidad:
 * - Muestra observaciones del cumplimiento (readonly)
 * - Muestra recomendaciones del cumplimiento (readonly)
 * - Consulta datos desde TPSC_CUMPLS_OBSERVACIONES y TPSC_CUMPLS_RECOMENDACION
 *
 * @author Emerson Rodriguez - Ingeneo
 */
import {
  Component,
  input,
  output,
  inject,
  signal,
  OnChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextarea } from 'primeng/inputtextarea';

import { environment } from 'src/environments/environment';
import { LoadingOverlayComponent } from 'src/app/presentation/shared/loading-overlay.component';
import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { HistorialCumplimiento } from 'src/app/domain/model/cumplimiento/cumplimiento';

@Component({
  selector: 'app-observaciones-recomendaciones-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextarea,
    LoadingOverlayComponent
  ],
  template: `
    <p-dialog
      header="Observaciones / Recomendaciones"
      [(visible)]="dialogVisible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '38rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCerrar()"
    >
      <app-loading-overlay [blocked]="loading()" />

      <div class="grid gap-4">
        <!-- Observaciones -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Observaciones
          </label>
          <textarea
            pInputTextarea
            [value]="observacion()"
            [readonly]="true"
            [rows]="6"
            class="w-full"
            placeholder="Sin observaciones registradas"
          >
          </textarea>
        </div>

        <!-- Recomendaciones -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Recomendaciones
          </label>
          <textarea
            pInputTextarea
            [value]="recomendacion()"
            [readonly]="true"
            [rows]="6"
            class="w-full"
            placeholder="Sin recomendaciones registradas"
          >
          </textarea>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <button
            pButton
            label="Cerrar"
            icon="pi pi-times"
            class="p-button-outlined p-button-secondary min-h-[44px]"
            (click)="onCerrar()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class ObservacionesRecomendacionesDialogComponent implements OnChanges {
  // === Inputs/Outputs ===

  /** Si el dialog está visible */
  visible = input<boolean>(false);

  /** Cumplimiento seleccionado */
  cumplimiento = input<HistorialCumplimiento | null>(null);

  /** Evento al cerrar el dialog */
  cerrar = output<void>();

  // === Services ===

  private readonly cumplimientoRepo = inject(CumplimientoRepository);

  // === State ===

  dialogVisible = false;
  loading = signal(false);
  observacion = signal('');
  recomendacion = signal('');

  // === Lifecycle ===

  ngOnChanges(): void {
    this.dialogVisible = this.visible();

    // Cargar datos cuando se abre el dialog
    if (this.visible() && this.cumplimiento()) {
      this.cargarDatos();
    }
  }

  // === Métodos ===

  /**
   * Carga observaciones y recomendaciones del cumplimiento.
   *
   * Optimizado: Una sola llamada HTTP en lugar de 2 separadas.
   * Legacy hacía 2 llamadas: getObservacionesCumplimiento() + getRecomendacionesCumplimiento()
   */
  private async cargarDatos(): Promise<void> {
    const cumpl = this.cumplimiento();
    if (!cumpl) return;

    this.loading.set(true);
    this.observacion.set('');
    this.recomendacion.set('');

    try {
      // Una sola llamada HTTP optimizada
      const { observacion, recomendacion } =
        await this.cumplimientoRepo.obtenerObservacionYRecomendacion(
          cumpl.nmconsecutivo
        );

      this.observacion.set(observacion || '');
      this.recomendacion.set(recomendacion || '');
    } catch (error) {
      if (!environment.production)
        console.error('Error al cargar observaciones/recomendaciones:', error);
      // No mostrar error al usuario, solo dejar vacíos los campos
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Cierra el dialog.
   */
  onCerrar(): void {
    this.dialogVisible = false;
    this.cerrar.emit();
  }
}
